import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IonDatetime, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Api } from 'src/smoothr-web-app-core/api/api';
import BoardingPassResponse from 'src/smoothr-web-app-core/models/BoardingPassResponse';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import { BarcodeFormat, Result } from '@zxing/library';
import { IonModal } from '@ionic/angular';
import { DatePipe } from '@angular/common';

enum SelectedInputType {
	SCAN = 'scan',
	MANUALLY = 'manually',
}

type boardingForm = {
	name: FormControl<string>;
	surname: FormControl<string>;
	date: FormControl<string>;
	airCode: FormControl<string>;
};

@Component({
	selector: 'app-scan-boarding-pass',
	templateUrl: './scan-boarding-pass.component.html',
	styleUrls: ['./scan-boarding-pass.component.scss'],
	standalone: false,
})
export class ScanBoardingPassComponent implements OnInit {
	private fb = inject(FormBuilder);
	private modalCtrl = inject(ModalController);
	private snackbarCtrl = inject(MatSnackBar);

	@ViewChild(IonModal) modal!: IonModal;

	venue: Venue;
	selectedInputType: SelectedInputType = SelectedInputType.SCAN;
	selectedInputTypeEnum = SelectedInputType;
	boardingDataForm: FormGroup<boardingForm>;
	barCode: string = '';
	loading = false;
	error = false;
	scanning = true;
	today = new Date().toISOString();

	availableDevices: MediaDeviceInfo[] = [];
	currentDevice: MediaDeviceInfo | undefined;
	private datePipe = new DatePipe('en-GB');

	formats = [
		BarcodeFormat.QR_CODE,
		BarcodeFormat.CODE_128,
		BarcodeFormat.CODE_39,
		BarcodeFormat.EAN_13,
		BarcodeFormat.EAN_8,
		BarcodeFormat.UPC_A,
		BarcodeFormat.UPC_E,
		BarcodeFormat.DATA_MATRIX,
		BarcodeFormat.AZTEC,
		BarcodeFormat.PDF_417,
	];

	constructor() {
		this.boardingDataForm = this.fb.nonNullable.group({
			name: ['', Validators.required],
			surname: ['', Validators.required],
			date: ['', Validators.required],
			airCode: ['', [Validators.required]],
		});
	}
	static async show(modalCtrl: ModalController, venue: Venue = null): Promise<BoardingPassResponse> {
		const modal = await modalCtrl.create({
			cssClass: 'boarding-pass auto-height',
			component: ScanBoardingPassComponent,
			componentProps: {
				venue,
			},
			showBackdrop: true,
			backdropDismiss: true,
		});
		await modal.present();
		const result = (await modal.onDidDismiss()).data;

		return result;
	}
	ngOnInit() {
		this.boardingDataForm.valueChanges.subscribe(v => {
			console.log(v);
		});
	}
	ngAfterViewInit(): void {}

	ionViewWillLeave() {
		this.scanning = false;
	}

	selectType(value: SelectedInputType) {
		this.selectedInputType = value;
	}

	onQrCodeResult(resultString: string) {
		console.log('QR Code Result: ', resultString);
	}
	startScanning() {
		this.scanning = true;
	}

	stopScanning() {
		this.scanning = false;
	}

	scanSuccessHandler(result: string) {
		if (!result) return;

		console.log('SCAN RESULT:', result);

		this.barCode = result;

		// stop camera instantly
		this.scanning = false;

		this.validateQrCode();
	}

	async manualClick() {
		if (!this.venue) {
			return;
		}
		this.loading = true;

		try {
			const formValue = this.boardingDataForm.value;
			console.log(formValue);

			const formData = {
				firstName: formValue.name,
				lastName: formValue.surname,
				airlineCode: formValue.airCode.trim().slice(0, 2),
				flightNumber: formValue.airCode.trim().slice(2, formValue.airCode.length).trim(),
				travelDate: formValue.date,
				venue: this.venue._id,
			};
			const response = await Api.validateBoardingPassManually(formData);
			console.log(response);

			if (response['error']) {
				throw new Error('wrong data');
			}

			this.successfulDismiss(response);
		} catch (e) {
			this.error = true;
		} finally {
			this.loading = false;
		}
	}
	async validateQrCode() {
		console.log('CLICK', this.venue, this.barCode);
		if (!this.venue) {
			return;
		}
		this.loading = true;

		try {
			this.scanning = false;
			const barCodeData = {
				barcodeData: this.barCode,
				venue: this.venue._id,
			};
			const response = await Api.validateBoardingPassQR(barCodeData);
			console.log(response);
			if (response['error']) {
				throw new Error('wrong data');
			}
			this.successfulDismiss(response);
			this.snackbarCtrl.open('Scanned successfully', '', {
				duration: 2000,
			});
		} catch (e) {
			this.scanning = true;
			this.error = true;
		} finally {
			this.loading = false;
		}
	}
	dismiss() {
		this.modalCtrl.dismiss();
	}

	successfulDismiss(result: BoardingPassResponse) {
		this.modalCtrl.dismiss(result);
	}

	onCamerasFound(devices: MediaDeviceInfo[]) {
		this.availableDevices = devices;

		console.log('Devices:', devices);
		const frontCamera = devices.find(device => /front|user|face/i.test(device.label));

		this.currentDevice = frontCamera || devices[0];
	}

	onPermission(hasPermission: boolean) {
		if (!hasPermission) {
			this.snackbarCtrl.open('Camera permission denied', '', {
				duration: 3000,
			});
		}
	}

	onDateChange(e: CustomEvent) {
		const value = this.datePipe.transform(e.detail.value, 'MM-dd-yyyy');
		this.boardingDataForm.patchValue({ date: value });
	}
}
